/*
  Desktop menu
*/

/*
  Sticky header
*/
const initStickyHeader = () => {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) {
    return;
  }

  headerGroup.classList.add("sticky");
  let lastScrollTop = 0;
  let ticking = false;
  const scrollThreshold = 12;

  const updateHeaderHeight = () => {
    document.documentElement.style.setProperty('--header-group-height', `${headerGroup.clientHeight}px`);
  };

  const onScroll = () => {
    if (ticking) {
      return;
    }

    ticking = true;

    window.requestAnimationFrame(() => {
      const currentScroll = window.pageYOffset;
      const scrollDelta = currentScroll - lastScrollTop;

      if (Math.abs(scrollDelta) < scrollThreshold) {
        ticking = false;
        return;
      }

      if (scrollDelta > 0 && currentScroll > headerGroup.clientHeight) {
        headerGroup.classList.add("hide");
      } else {
        headerGroup.classList.remove("hide");
      }

      lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
      ticking = false;
    });
  }

  updateHeaderHeight();
  window.addEventListener("resize", updateHeaderHeight);
  window.addEventListener("scroll", onScroll, { passive: true });
}
initStickyHeader();


/*
  Mobile menu
*/
const initMobileMenu = () => {
  const headerMobile = document.querySelector('#header-mobile');
  const mobileMenu = document.querySelector('#header-mobile-menu');
  const toggleButton = document.querySelector('[data-toggle="header-mobile-menu"]'); 

  if (!mobileMenu || !toggleButton) {
    return;
  }

  const scrollToActiveCollectionItem = () => {
    const collectionList = mobileMenu.querySelector('.collection-list');
    const activeCollectionItem = collectionList?.querySelector('a.active');

    if (!collectionList || !activeCollectionItem) {
      return;
    }

    const listRect = collectionList.getBoundingClientRect();
    const activeRect = activeCollectionItem.getBoundingClientRect();
    const offset = activeRect.left - listRect.left - (listRect.width / 2) + (activeRect.width / 2);
    collectionList.scrollLeft += offset;
  };

  if (mobileMenu.hidden) {
    mobileMenu.style.visibility = 'hidden';
    mobileMenu.hidden = false;
    scrollToActiveCollectionItem();
    mobileMenu.hidden = true;
    mobileMenu.style.visibility = '';
  } else {
    scrollToActiveCollectionItem();
  }

  const shouldAnimateMobileMenu = () => (
    mobileMenu.dataset.animateOnScroll === 'true' &&
    document.documentElement.dataset.designMode !== 'true' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const revealMobileMenuAnimations = () => {
    if (!mobileMenu.hasAttribute('data-animate-on-scroll')) return;

    if (!shouldAnimateMobileMenu()) {
      mobileMenu.dataset.viewportEntered = 'true';
      return;
    }

    mobileMenu.dataset.viewportEntered = 'false';
    mobileMenu.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      mobileMenu.dataset.viewportEntered = 'true';
    });
  };

  const openMenu = () => {
    toggleButton.setAttribute('aria-expanded', 'true');
    const headerGroup = document.querySelector('#header-group');
    const headerHeight = headerGroup ? headerGroup.clientHeight : 0;
    mobileMenu.style.setProperty('--top', `${headerHeight}px`);
    mobileMenu.hidden = false;
    revealMobileMenuAnimations();
    document.body.style.overflow = 'hidden';
    
    headerMobile.querySelectorAll('a, button').forEach((el) => {
      if (el !== toggleButton) {
        el.setAttribute('tabindex', '-1');
      }
    });
  };

  const closeMenu = () => {
    toggleButton.setAttribute('aria-expanded', 'false');
    toggleButton.focus();
    mobileMenu.hidden = true;
    if (mobileMenu.hasAttribute('data-animate-on-scroll')) {
      mobileMenu.dataset.viewportEntered = 'false';
    }
    document.body.style.overflow = '';

    headerMobile.querySelectorAll('a, button').forEach((el) => {
      el.removeAttribute('tabindex');
    });
  };

  toggleButton.addEventListener('click', () => {
    const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  mobileMenu.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !event.target.closest('.submenu')) {
      closeMenu();
    }
  });

  document.addEventListener('click', (event) => {
    const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
    if (isExpanded && !mobileMenu.contains(event.target) && !toggleButton.contains(event.target)) {
      closeMenu();
    }
  });

  const submenuToggles = mobileMenu.querySelectorAll('[data-submenu-toggle]');
  const submenus = mobileMenu.querySelectorAll('.submenu');
  const backButtons = mobileMenu.querySelectorAll('[data-back-button]');

  submenuToggles.forEach((toggle) => {
    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      const isKeyboard = event.detail === 0;
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isExpanded));
      if (isKeyboard && !isExpanded) {
        const submenu = toggle.nextElementSibling;
        setTimeout(() => {
          submenu.querySelector('a, button')?.focus();
        }, 300);
      }
    });
  });

  submenus.forEach((submenu) => {
    submenu.addEventListener('keydown', (event) => {
      const toggle = submenu.previousElementSibling
      if (event.key === 'Escape') {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });
  });

  backButtons.forEach((button) => {
    button.addEventListener('click', () => {
      setTimeout(() => {
        const submenu = button.closest('.submenu');
        const toggle = submenu.previousElementSibling;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }, 200);
    });
  });

  mobileMenu.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    
    let focusableElements, firstElement, lastElement;

    if (event.target.closest('.submenu')) {
      const submenu = event.target.closest('.submenu');
      focusableElements = submenu.querySelectorAll('a, button');
      firstElement = focusableElements[0];
      lastElement = focusableElements[focusableElements.length - 1];
    } else {
      mobileMenu.querySelectorAll('a, button').forEach((el) => {
        if (!el.closest('.submenu')) {
          el.setAttribute('tabindex', '0');
        }
      });
      focusableElements = mobileMenu.querySelectorAll('a, button');
      firstElement = toggleButton;
      lastElement = focusableElements[focusableElements.length - 1];
    }

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  });
}
initMobileMenu();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#header-mobile-menu')) {
    initMobileMenu();
  }
});


/*
  Desktop menu
*/
const initDesktopMenu = () => {
  const desktopMenu = document.querySelector('#header-desktop-menu');

  if (!desktopMenu) {
    return;
  }

  const dropdownToggles = desktopMenu.querySelectorAll('[data-dropdown-toggle]');
  const dropdownMenus = desktopMenu.querySelectorAll('.dropdown-menu, .dropdown-megamenu');

  dropdownToggles.forEach((toggle) => {
    toggle.addEventListener('click', (event) => {

      dropdownToggles.forEach((btn) => {
        if (btn !== toggle) {
          btn.setAttribute('aria-expanded', 'false');
        }
      });

      event.preventDefault();
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isExpanded));
    });
  });

  dropdownMenus.forEach((dropdownMenu) => {
    dropdownMenu.addEventListener('keydown', (event) => {
      const toggle = dropdownMenu.previousElementSibling
      if (event.key === 'Escape') {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });
  });
  
  document.addEventListener('click', (event) => {
    if (!desktopMenu.contains(event.target)) {
      dropdownToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }
  });
}
initDesktopMenu();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#header-desktop-menu')) {
    initDesktopMenu();
  }
});
  
